/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useId, useState } from 'react'
import Highcharts from 'highcharts/highmaps'
import { pewPewMapLinesMapper, pewPewMapPointsMapper } from '../../../utils/malware-mapper'
import { PewPewObject, SourceToTargetPewPew } from '../../../types/StatTypes'
import ChartSkeleton from '../../skeleton/ChartSkeleton'
import BaseChart from '../../charts/BaseChart'

interface IVisualizationsPewPewMapProps {
  chartData: SourceToTargetPewPew[]
  mapData: object
}

const VisualizationsPewPewMap = ({ chartData, mapData }: IVisualizationsPewPewMapProps) => {
  const [filteredData, setFilteredData] = useState<SourceToTargetPewPew[]>(chartData)
  const [mappedData, setMappedData] = useState<PewPewObject>({ lines: [], points: {} })
  const [selectedSource, setSelectedSource] = useState<string | null>(null)
  const chartId = useId()

  useEffect(() => {
    setMappedData({ lines: [], points: {} })

    setTimeout(() => {
      const lineColor = selectedSource ? '#f03e3e' : '#D3D3D380'
      const lines = pewPewMapLinesMapper(filteredData, lineColor)
      const points = pewPewMapPointsMapper(filteredData)
      setMappedData({ lines, points })
    }, 0)
  }, [filteredData])

  const handlePointClick = (event: Highcharts.PointClickEventObject) => {
    if (selectedSource) return

    const clickedCountry = event.point.name.trim().toLowerCase()
    setMappedData({ lines: [], points: {} })
    setFilteredData([])
    setSelectedSource(clickedCountry)

    setTimeout(() => {
      const newFilteredData = chartData.filter((data) => data.source_country.trim().toLowerCase() === clickedCountry)
      setFilteredData(newFilteredData)
    }, 0)
  }

  const generateTooltipData = (mapCountry: string) => {
    if (!selectedSource) {
      return `<b>${mapCountry}</b><br>Click to see target countries`
    }

    if (mapCountry.trim().toLowerCase() === selectedSource) {
      return `<b>${mapCountry}</b><br>Source Country`
    }

    const matchingTargets = filteredData.filter(
      (data) => data.target_country.trim().toLowerCase() === mapCountry.trim().toLowerCase()
    )

    if (matchingTargets.length === 0) return `<b>${mapCountry}</b><br>No threats detected`

    return `<b>${mapCountry}</b><br>Total Threats: ${matchingTargets.reduce(
      (sum, data) => sum + data.total_threats,
      0
    )}`
  }

  const resetMap = () => {
    setMappedData({ lines: [], points: {} })
    setFilteredData([])
    setSelectedSource(null)

    setTimeout(() => {
      setFilteredData(chartData)
    }, 0)
  }

  const options = {
    chart: {
      height: '1000px',
      backgroundColor: 'transparent',
    },
    title: {
      text: null,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    mapNavigation: {
      enabled: true,
      enableDoubleClickZoomTo: true,
      buttonOptions: {
        verticalAlign: 'bottom',
      },
    },
    mapView: {
      projection: {
        name: 'Miller',
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function (this: any): string {
        return generateTooltipData(this.point?.name)
      },
    },
    plotOptions: {
      series: {
        animationLimit: 500,
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
      mapline: {
        enableMouseTracking: false,
      },
      mappoint: {
        cursor: 'pointer',
        point: {
          events: {
            click: handlePointClick,
          },
        },
      },
    },
    series: [
      {
        mapData: mapData,
        joinBy: null,
        name: 'Base Map',
        dataLabels: {
          enabled: false,
          format: '{point.name}',
        },
        clip: false,
      },
      ...mappedData.lines,
      {
        ...mappedData.points,
        joinBy: null,
        boostThreshold: 1,
        turboThreshold: 0,
      },
    ],
  }

  return (
    <>
      {mapData && chartData && (
        <div style={{ position: 'relative' }}>
          <BaseChart options={options} id={chartId} isMap={true} />

          <button
            onClick={() => resetMap()}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              zIndex: 1000,
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
            }}
          >
            Reset Map
          </button>
        </div>
      )}
      {(!mapData || !chartData) && <ChartSkeleton fullWidth />}
    </>
  )
}

export default VisualizationsPewPewMap
